import { Component, type ReactNode } from 'react';
import { InPartCardContext } from './DossierPrimitives';

/**
 * One Part of a complete device dossier page (EU MDR / EU IVDR via CombinedEuDossierView, NI IVDR
 * via CombinedNiIvdrDossierView): the Part heading, then ONE card holding its sections as headed
 * blocks — as the CTD view draws one card per Module.
 */

/** One fully-resolved section ready to render: heading + body. */
export interface RenderedSection {
  id: string;
  title: string;
  body: ReactNode;
}

/** Per-section error boundary: a single malformed part must never crash the
 *  whole dossier page. On a render error it shows a muted fallback (the section
 *  is still in the Download PDF) and logs which view + section + why for diagnosis. */
class SectionErrorBoundary extends Component<
  { view: string; sectionTitle: string; children: ReactNode },
  { errored: boolean }
> {
  state = { errored: false };
  static getDerivedStateFromError() {
    return { errored: true };
  }
  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.view}] section "${this.props.sectionTitle}" failed to render:`, error);
  }
  render() {
    if (this.state.errored) {
      return (
        <p className="text-[13px] text-muted m-0">
          This section couldn't be displayed here — it's included in the downloadable PDF.
        </p>
      );
    }
    return this.props.children;
  }
}

/** One rendered Part (heading + its single card). An optional `action` renders
 *  right-aligned on the heading row — same pattern as the US FDA result page,
 *  where the "Download PDF" button sits to the right of the dossier heading. */
export function SectionBlock({
  view,
  section,
  action,
}: {
  /** The page's name, for the error log. */
  view: string;
  section: RenderedSection;
  action?: ReactNode;
}) {
  return (
    <section id={section.id} className="scroll-mt-24">
      <div className="flex items-center justify-between gap-3 mb-[14px]">
        <h2 className="text-[22px] font-bold text-heading -tracking-[0.2px] m-0">
          {section.title}
        </h2>
        {action ? <div className="flex items-center gap-2 shrink-0">{action}</div> : null}
      </div>
      <div className="bg-white border border-line rounded-xl p-[22px]">
        <InPartCardContext.Provider value>
          <SectionErrorBoundary view={view} sectionTitle={section.title}>
            {section.body}
          </SectionErrorBoundary>
        </InPartCardContext.Provider>
      </div>
    </section>
  );
}
